import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SVGIcon, calendarIcon, copyIcon, filePdfIcon, fileTxtIcon } from '@progress/kendo-svg-icons';
import * as saveAs from 'file-saver';
import { forkJoin, switchMap } from 'rxjs';

import { CFAttachmentFile } from '../../models/attachments/cf-attachment-file';
import { CFFile } from '../../models/attachments/cf-file';
import { CFDocumentFile } from '../../models/cf-document/cf-document-file';
import { CFDocumentToDisplay } from '../../models/cf-document/cf-document-to-display';
import { CFSectionToDisplay } from '../../models/cf-document/cf-section-to-display';
import { TaskToDisplay } from '../../models/company-tasks/task-to-display';
import { CFEventToDisplay } from '../../models/events/cf-event-to-display';
import { KeyValuePairEnum } from '../../models/key-value-pair-enum';
import { CFAttachmentService } from '../../services/attachments/cf-attachment.service';
import { CustomNotificationService } from '../../services/custom-notification.service';
import { CFDocumentStoreService } from '../../services/documents/cf-document-store.service';
import { CFDocumentService } from '../../services/documents/cf-document.service';
import { CFEventService } from '../../services/events/cf-event.service';
import { CFSignatureStoreService } from '../../services/signatures/cf-signature-store.service';
import { CustomMessagesService } from '../../services/translation/custom-messages.service';
import { Constants } from '../../util/constants';

@UntilDestroy()
@Component({
    selector: 'clfl-cf-document',
    templateUrl: './cf-document.component.html',
    styleUrls: ['./cf-document.component.scss']
})
export class CfDocumentComponent implements OnInit {
    public fileTxtIcon: SVGIcon = fileTxtIcon;
    public filePdfIcon: SVGIcon = filePdfIcon;
    public copyIcon: SVGIcon = copyIcon;
    public calendarIcon: SVGIcon = calendarIcon;

    public topPaneSize: string = localStorage.getItem('cfDocumentMainSplitterSize') || '400px';
    public isSignWithPinDialogOpened = false;
    public isSignOnGlassDialogOpened = false;
    public isEventRegistrationDialogOpened = false;

    public documentTypes!: Array<KeyValuePairEnum>;
    public document!: CFDocumentToDisplay;
    public errorMessage: string | undefined;
    public isLoading = false;
    public isDocumentLoaded = false;
    public selectedTask: TaskToDisplay;

    public documentFiles!: Array<CFDocumentFile>;
    public attachmentFiles!: Array<CFAttachmentFile>;
    public events!: Array<CFEventToDisplay>;

    public leftSideExpanded = true;

    constructor(
        private activeRoute: ActivatedRoute,
        private router: Router,
        private documentService: CFDocumentService,
        private attachmentService: CFAttachmentService,
        private eventService: CFEventService,
        private notificationService: CustomNotificationService,
        private customMsgService: CustomMessagesService,
        public documentStoreService: CFDocumentStoreService,
        public signatureStoreService: CFSignatureStoreService
    ) {}

    ngOnInit(): void {
        this.documentStoreService.documentTypes$.pipe(untilDestroyed(this)).subscribe({
            next: (types) => (this.documentTypes = types)
        });

        this.documentStoreService.resetFilters();
        this.documentStoreService.selectedDocumentType = parseInt(this.activeRoute.snapshot.paramMap.get('documentType'));
        this.documentStoreService.documentNumber = this.activeRoute.snapshot.paramMap.get('documentNumber');
        this.documentStoreService.companyId = this.activeRoute.snapshot.paramMap.get('companyId');
        this.isDocumentLoaded = false;
        this.errorMessage = undefined;
        if (isNaN(this.documentStoreService.selectedDocumentType)) {
            this.documentStoreService.selectedDocumentType = 0;
        } else {
            this.searchDocument();
        }
    }

    public expandColapseLeftSide() {
        this.leftSideExpanded = !this.leftSideExpanded;
    }

    public onSearch() {
        this.router.navigate(
            [
                'cf-document',
                this.documentStoreService.selectedDocumentType,
                this.documentStoreService.documentNumber,
                this.documentStoreService.companyId
            ],
            { relativeTo: this.activeRoute.root }
        );
        this.searchDocument();
    }

    public searchDocument() {
        this.errorMessage = undefined;
        this.isLoading = true;
        this.isDocumentLoaded = false;

        this.documentService
            .getDocument(
                this.documentStoreService.selectedDocumentType,
                this.documentStoreService.companyId,
                this.documentStoreService.documentNumber
            )
            .pipe(
                untilDestroyed(this),
                switchMap((document: CFDocumentToDisplay) => {
                    if (document) {
                        this.document = document;
                        this.documentStoreService.setDocumentData(document);

                        const events$ = this.eventService.getEvents(this.document.cloudFlowId);

                        const documentVersions$ = this.documentService.getDocumentVersions(
                            this.documentStoreService.selectedDocumentType,
                            this.documentStoreService.companyId,
                            this.document.externalId
                        );

                        const attachments$ = this.attachmentService.getAttachments(
                            this.documentStoreService.selectedDocumentType,
                            this.documentStoreService.companyId,
                            this.document.externalId
                        );

                        return forkJoin([events$, documentVersions$, attachments$]);
                    }

                    return forkJoin([]);
                })
            )
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.notificationService.success(this.customMsgService.translate('cf-document.notification.documentFound'));
                        this.isDocumentLoaded = true;

                        this.events = response[0];
                        this.documentFiles = response[1];
                        this.attachmentFiles = response[2];
                    }
                },
                error: (err) => {
                    this.errorMessage = this.customMsgService.translate('cf-document.notification.documentError');
                },
                complete: () => {
                    this.isLoading = false;
                }
            });
    }

    public getDocumentVersions() {
        this.documentService
            .getDocumentVersions(
                this.documentStoreService.selectedDocumentType,
                this.documentStoreService.companyId,
                this.document.externalId
            )
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (files) => {
                    this.documentFiles = files;
                }
            });
    }

    public getAttachmentFiles() {
        this.attachmentService
            .getAttachments(this.documentStoreService.selectedDocumentType, this.documentStoreService.companyId, this.document.externalId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (files) => {
                    this.attachmentFiles = files;
                }
            });
    }

    public getEvents() {
        this.eventService
            .getEvents(this.document.cloudFlowId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (events) => {
                    this.events = events;
                }
            });
    }

    public onFileDownload(file: CFFile) {
        this.attachmentService
            .downloadFile(
                this.documentStoreService.selectedDocumentType,
                this.documentStoreService.companyId,
                this.document.externalId,
                file.ipfsId
            )
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (response: any) => saveAs(response, file.fileName.concat(file.extension)),
                error: (err) => {
                    this.notificationService.handleError(this.customMsgService.translate('cf-document.tab.files.column.downloadError'));
                },
                complete: () =>
                    this.notificationService.success(this.customMsgService.translate('cf-document.tab.files.column.downloadSuccess'))
            });
    }

    public spliterSizeChanged(newSize: string): void {
        localStorage.setItem('cfDocumentMainSplitterSize', newSize);
    }

    public isDocumentTypeSelected(): boolean {
        return this.documentTypes && this.documentStoreService.selectedDocumentType !== undefined ? true : false;
    }

    public setLoadingState(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    public anyUnsignedSection(): boolean {
        const unsignedSections = this.document.sections.filter((section) => !section.isSigned);

        return unsignedSections.length > 0;
    }

    public isDocumentCanceled(): boolean {
        return this.document.documentState.toLowerCase() === Constants.DOCUMENT_STATE_CANCELED.toLowerCase();
    }

    public openSignDialog() {
        switch (this.signatureStoreService.selectedSignatureType) {
            case 0:
                this.isSignWithPinDialogOpened = true;
                break;
            case 1:
                this.isSignOnGlassDialogOpened = true;
                break;
            default:
                this.isSignWithPinDialogOpened = true;
                break;
        }
    }

    public onDocumentNumberBlur() {
        this.documentStoreService.documentNumber = this.documentStoreService.documentNumber.trim();
    }

    public onCompanyIdBlur() {
        this.documentStoreService.companyId = this.documentStoreService.companyId.trim();
    }

    public openRegisterEventDialog(task: TaskToDisplay) {
        this.isEventRegistrationDialogOpened = true;
        this.selectedTask = task;
    }

    public getUnsignedSections(): Array<CFSectionToDisplay> {
        return this.document.sections.filter((section) => !section.isSigned);
    }
}
